import orderBy from 'lodash/orderBy';
import noop from 'lodash/noop';
import { createNamespacedHelpers } from 'vuex';
import { Firebase } from '@/api/firebase';
import ChannelItem from '@/components/channel_item/ChannelItem';
import ChannelDefs from '@/store/channel/channel.d';
import IconYoga from '@/assets/icons/yoga.svg';
import IconDrink from '@/assets/icons/drink.svg';
import Audio from '@/assets/audio';
import FavIco from 'favico.js-slevomat';
import subscription from '@/api/subscription';

const {
  mapState: mapChannelState,
  mapActions: mapChannelActions,
  mapMutations: mapChannelMutations
} = createNamespacedHelpers('Channel');

const { mapState: mapConciergeState } = createNamespacedHelpers('Concierge');
const favicon = new FavIco({
  animation: 'none'
});

export default {
  name: 'ChannelList',
  components: { ChannelItem, IconYoga, IconDrink },
  props: {
    tabName: String,
    conciergeId: String
  },
  mounted: function() {
    this.$notification.requestPermission();
    this.loadChannels(this.tabName);
    favicon.badge(0);
  },
  beforeDestroy: function() {
    this.channelListener.off('value');
  },
  data: function() {
    return {
      premiumRecords: {},
      waitingFirstLoadedStamp: 0,
      selectedChannelId: '',
      channels: [],
      channelListener: {
        off: noop
      }
    };
  },
  computed: {
    ...mapChannelState({
      currentChannel: state => state.currentChannel,
      unreadChannels: state => state.unreadChannels,
      getCurrentChannelURL: function(state) {
        return state.currentChannel ? state.currentChannel.channelUrl : '';
      }
    }),

    ...mapConciergeState({
      currentConcierge: state => state.currentConcierge
    }),

    orderedChannels: function() {
      const { channels = [] } = this;
      const formattedChannels = channels.map(
        ({ rewards = [], ...channel }) => ({
          ...channel,
          premiumOrder: channel.isPremium ? 2 : 1,
          totalRewards: rewards.reduce(
            (totalRewards, { rewardCount }) => totalRewards + rewardCount,
            0
          )
        })
      );

      return orderBy(
        formattedChannels,
        ['premiumOrder', 'totalRewards', 'last_message_activity'],
        ['desc', 'desc', 'desc']
      );
    }
  },
  watch: {
    channels: function() {
      const userIds = this.channels.map(channel => channel.sender.user_id);
      subscription.getSubscriptionByUsers(userIds).then(response => {
        this.premiumRecords = Object.assign(
          {},
          this.premiumRecords,
          response || {}
        );
      });
      this.$emit('channel-count', {
        tabName: this.tabName,
        count: this.channels.length,
        conciergeId: this.conciergeId
      });
    },

    currentConcierge: function() {
      if (this.tabName === 'own') {
        this.loadOwnChannels();
      }
    },

    // FIXME: better move it somewhere, on vuex level watch?
    unreadChannels: function() {
      const count = Object.keys(this.unreadChannels).length || 0;
      favicon.badge(count);
    },

    // FIXME: better move it somewhere, on vuex level watch?
    getCurrentChannelURL: function() {
      const channelUrl = this.getCurrentChannelURL;
      if (!this.unreadChannels[channelUrl]) return;
      this.setChannelUnread({
        channelUrl,
        isUnread: false
      });
    }
  },
  methods: {
    ...mapChannelActions({
      channelStore_setOwnChannels: ChannelDefs.actions.setOwnChannels,
      channelStore_joinChannel: ChannelDefs.actions.joinChannel
    }),

    ...mapChannelMutations({
      setChannelUnread: ChannelDefs.mutations.setChannelUnread
    }),

    loadChannels: function() {
      switch (this.tabName) {
        case 'own':
          this.loadOwnChannels();
          break;

        case 'waiting':
          this.loadWaitingChannels();
          break;

        case 'archived':
          this.loadArchivedChannels();
          break;

        default:
          break;
      }
    },

    // eslint-disable-next-line complexity
    handleOnNewMessage: function(snapshot) {
      const channel = snapshot.val() || {};
      const {
        last_message_activity,
        channel: { channel_url: channelUrl }
      } = channel;
      const { created_at } = channel.payload;

      if (created_at !== last_message_activity) return;
      if (channelUrl !== this.getCurrentChannelURL) {
        this.setChannelUnread({
          channelUrl,
          isUnread: true
        });
      }
      Audio.newMessage.play();
      if (!document.hidden) return;

      const options = {
        body: channel.payload.message,
        icon: channel.sender.profile_url
      };

      this.$notification.show(channel.sender.nickname, options, {});
    },

    loadOwnChannels: function() {
      const { agentId } = this.currentConcierge;
      if (this.channelListener.ref || !agentId) return;

      this.channelListener = Firebase.getConciergeChannels(agentId);
      this.channelListener.on('value', snapshot => {
        const values = snapshot.val() || {};
        this.channels = Object.values(values);
        this.channelStore_setOwnChannels(this.channels);
      });

      this.channelListener.on('child_changed', this.handleOnNewMessage);
    },

    handleOnIncomingChat: function(snapshot) {
      const channel = snapshot.val() || {};
      const { last_message_activity } = channel;
      const { profile_url, nickname } = channel.sender;

      if (last_message_activity < this.waitingFirstLoadedStamp) return;
      if (!document.hidden) return;

      const options = { icon: profile_url };
      this.$notification.show(`Incoming Chat: ${nickname}`, options, {});
      Audio.incomingChat.play();
    },

    loadWaitingChannels: function() {
      if (this.channelListener.ref) return;

      this.channelListener = Firebase.getWaitingChannels();
      this.channelListener.on('value', snapshot => {
        this.channels = Object.values(snapshot.val() || {});

        if (this.waitingFirstLoadedStamp === 0) {
          this.waitingFirstLoadedStamp = Date.now();
          this.channelListener.on('child_added', this.handleOnIncomingChat);
        }
      });
    },

    loadArchivedChannels: function() {
      if (!this.channelListener.ref) {
        this.channelListener = Firebase.getArchivedChannels(
          this.currentConcierge.agentId
        );
        this.channelListener.on('value', channels => {
          this.channels = Object.values(channels.val() || {}).filter(
            channel => {
              return channel.status == 'archived';
            }
          );
        });
      }
    }
  }
};
